// SCR-STAFF-889 — گزارش‌های دفتر جامع (US-ACC-061 تا 063؛ AP-ACC-03): تراز آزمایشی دو، چهار یا شش ستونی در سه سطح کدینگ؛ ترازنامه و صورت
// سود و زیان با مقایسه دوره قبل هم‌طول؛ مرور حساب یا تفصیلی با مانده پس از هر ردیف و Drill-down به سند. گزارش‌های تجمعی از آخرین سند
// افتتاحیه یا بازگشایی کف می‌گیرند تا مانده حساب‌های دائم دو بار شمرده نشود (C-AGENT-1776).
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AccFinancialStatementSection,
  type AccTrialBalanceColumns,
  type AccTrialBalanceLevel,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DetailPage,
  EmptyState,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  LoadingState,
  toBusinessDate,
  type DetailTab,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import {
  accKeys,
  Amount,
  journalPath,
  num,
  SCR_STAFF_889,
  useAccBranches,
} from './common';

const LEVELS: readonly AccTrialBalanceLevel[] = ['General', 'Subsidiary', 'Detail'];
const COLUMNS: readonly AccTrialBalanceColumns[] = ['Two', 'Four', 'Six'];
const SECTIONS: readonly AccFinancialStatementSection[] = [
  'Asset',
  'Liability',
  'Equity',
  'Revenue',
  'Expense',
];

function yearStart(): string {
  const today = toBusinessDate(new Date()) ?? '';
  return today.length >= 8 ? `${today.slice(0, 4)}0101` : today;
}

interface Range {
  from: string | null;
  to: string | null;
}

export function LedgerReportsPage() {
  const { t } = useTranslation('acc');
  const branches = useAccBranches();
  const [tab, setTab] = useState('trial');
  const [branchId, setBranchId] = useState('');
  const { control, watch } = useForm<Range>({
    defaultValues: { from: yearStart(), to: toBusinessDate(new Date()) },
  });
  const from = watch('from') ?? yearStart();
  const to = watch('to') ?? from;
  const filters = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
      <JalaliDatePickerRHF control={control} name="from" labelKey="acc:common.from" />
      <JalaliDatePickerRHF control={control} name="to" labelKey="acc:common.to" />
      <TextField
        select
        size="small"
        label={t('acc:common.branch')}
        value={branchId}
        onChange={(event) => setBranchId(event.target.value)}
        sx={{ minWidth: 160 }}
        slotProps={{ htmlInput: { 'data-testid': 'acc-ledger-branch' } }}
      >
        <MenuItem value="">{t('acc:common.all')}</MenuItem>
        {branches.map((branch) => (
          <MenuItem key={branch.branchId} value={branch.branchId}>
            {branch.name}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
  const tabs: readonly DetailTab[] = [
    {
      id: 'trial',
      labelKey: 'acc:trial.title',
      content: (
        <>
          {filters}
          <TrialBalanceTab from={from} to={to} branchId={branchId} />
        </>
      ),
    },
    {
      id: 'statements',
      labelKey: 'acc:statements.title',
      content: (
        <>
          {filters}
          <StatementsTab from={from} to={to} branchId={branchId} />
        </>
      ),
    },
    {
      id: 'ledger',
      labelKey: 'acc:ledger.title',
      content: (
        <>
          {filters}
          <AccountLedgerTab from={from} to={to} />
        </>
      ),
    },
  ];
  return (
    <DetailPage
      screenId={SCR_STAFF_889}
      titleKey="acc:ledger.page_title"
      subtitleKey="acc:ledger.page_subtitle"
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    />
  );
}

function TrialBalanceTab({
  from,
  to,
  branchId,
}: {
  from: string;
  to: string;
  branchId: string;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const [level, setLevel] = useState<AccTrialBalanceLevel>('General');
  const [columns, setColumns] = useState<AccTrialBalanceColumns>('Four');
  const report = useQuery({
    queryKey: accKeys.trialBalance(session?.tenantId, `${from}|${to}|${branchId}|${level}|${columns}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/trial-balance', {
          params: { query: { from, to, level, columns, branchId: branchId || undefined } },
        }),
      ),
  });
  const data = report.data;
  const showOpening = columns !== 'Two';
  const showClosing = columns === 'Six' || columns === 'Four';
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label={t('acc:trial.level')}
          value={level}
          onChange={(event) => setLevel(event.target.value as AccTrialBalanceLevel)}
          sx={{ minWidth: 160 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-trial-level' } }}
        >
          {LEVELS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`acc:trial.levels.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('acc:trial.columns')}
          value={columns}
          onChange={(event) => setColumns(event.target.value as AccTrialBalanceColumns)}
          sx={{ minWidth: 160 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-trial-columns' } }}
        >
          {COLUMNS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`acc:trial.column_counts.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {data && data.rows.length === 0 ? <EmptyState titleKey="acc:report.empty" /> : null}
      {data && data.rows.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="acc-trial-balance">
          <Alert
            severity={data.isBalanced ? 'success' : 'error'}
            sx={{ mb: 2 }}
            data-testid="acc-trial-balanced"
            data-balanced={String(data.isBalanced)}
          >
            {t(data.isBalanced ? 'acc:trial.balanced' : 'acc:trial.unbalanced')}
          </Alert>
          <Table size="small" aria-label={t('acc:trial.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('acc:common.code')}</TableCell>
                <TableCell>{t('acc:common.title')}</TableCell>
                {showOpening ? (
                  <>
                    <TableCell align="right">{t('acc:trial.opening_debit')}</TableCell>
                    <TableCell align="right">{t('acc:trial.opening_credit')}</TableCell>
                  </>
                ) : null}
                <TableCell align="right">{t('acc:trial.period_debit')}</TableCell>
                <TableCell align="right">{t('acc:trial.period_credit')}</TableCell>
                {showClosing ? (
                  <>
                    <TableCell align="right">{t('acc:trial.closing_debit')}</TableCell>
                    <TableCell align="right">{t('acc:trial.closing_credit')}</TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.code} data-testid="acc-trial-row" data-code={row.code}>
                  <TableCell>
                    <bdi dir="ltr">{row.code}</bdi>
                  </TableCell>
                  <TableCell>{row.title}</TableCell>
                  {showOpening ? (
                    <>
                      <TableCell align="right">
                        <Amount money={row.openingDebit} />
                      </TableCell>
                      <TableCell align="right">
                        <Amount money={row.openingCredit} />
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell align="right">
                    <Amount money={row.periodDebit} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount money={row.periodCredit} />
                  </TableCell>
                  {showClosing ? (
                    <>
                      <TableCell align="right">
                        <Amount money={row.closingDebit} />
                      </TableCell>
                      <TableCell align="right">
                        <Amount money={row.closingCredit} />
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              ))}
              <TableRow data-testid="acc-trial-totals">
                <TableCell colSpan={2}>{t('acc:trial.totals')}</TableCell>
                {showOpening ? (
                  <>
                    <TableCell align="right">
                      <Amount money={data.totals.openingDebit} />
                    </TableCell>
                    <TableCell align="right">
                      <Amount money={data.totals.openingCredit} />
                    </TableCell>
                  </>
                ) : null}
                <TableCell align="right">
                  <Amount money={data.totals.periodDebit} testId="acc-trial-total-debit" />
                </TableCell>
                <TableCell align="right">
                  <Amount money={data.totals.periodCredit} testId="acc-trial-total-credit" />
                </TableCell>
                {showClosing ? (
                  <>
                    <TableCell align="right">
                      <Amount money={data.totals.closingDebit} />
                    </TableCell>
                    <TableCell align="right">
                      <Amount money={data.totals.closingCredit} />
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      ) : null}
    </Stack>
  );
}

function StatementsTab({ from, to, branchId }: { from: string; to: string; branchId: string }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const report = useQuery({
    queryKey: accKeys.financials(session?.tenantId, `${from}|${to}|${branchId}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/financial-statements', {
          params: { query: { from, to, branchId: branchId || undefined } },
        }),
      ),
  });
  const data = report.data;
  return (
    <Stack spacing={2}>
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {data && data.rows.length === 0 ? <EmptyState titleKey="acc:report.empty" /> : null}
      {data && data.rows.length > 0 ? (
        <>
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="acc-statements-summary">
            <Alert
              severity={data.isBalanced ? 'success' : 'error'}
              sx={{ mb: 2 }}
              data-testid="acc-statements-balanced"
              data-balanced={String(data.isBalanced)}
            >
              {t(data.isBalanced ? 'acc:statements.balanced' : 'acc:statements.unbalanced')}
            </Alert>
            <Table size="small" aria-label={t('acc:statements.title')}>
              <TableBody>
                <TableRow>
                  <TableCell>{t('acc:statements.total_assets')}</TableCell>
                  <TableCell align="right">
                    <Amount money={data.totalAssets} testId="acc-statements-assets" />
                  </TableCell>
                  <TableCell>{t('acc:statements.total_liabilities')}</TableCell>
                  <TableCell align="right">
                    <Amount money={data.totalLiabilities} testId="acc-statements-liabilities" />
                  </TableCell>
                  <TableCell>{t('acc:statements.total_equity')}</TableCell>
                  <TableCell align="right">
                    <Amount money={data.totalEquity} testId="acc-statements-equity" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('acc:statements.total_revenue')}</TableCell>
                  <TableCell align="right">
                    <Amount money={data.totalRevenue} testId="acc-statements-revenue" />
                  </TableCell>
                  <TableCell>{t('acc:statements.total_expense')}</TableCell>
                  <TableCell align="right">
                    <Amount money={data.totalExpense} testId="acc-statements-expense" />
                  </TableCell>
                  <TableCell>{t('acc:statements.net_result')}</TableCell>
                  <TableCell align="right">
                    <Amount money={data.netResult} testId="acc-statements-net" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
          {SECTIONS.filter((section) => data.rows.some((row) => row.section === section)).map((section) => (
            <Paper
              key={section}
              variant="outlined"
              sx={{ p: 2 }}
              data-testid="acc-statements-section"
              data-section={section}
            >
              <Typography variant="subtitle1" gutterBottom>
                {t(`acc:statements.sections.${section}`)}
              </Typography>
              <Table size="small" aria-label={t(`acc:statements.sections.${section}`)}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('acc:common.code')}</TableCell>
                    <TableCell>{t('acc:common.title')}</TableCell>
                    <TableCell align="right">{t('acc:statements.amount')}</TableCell>
                    <TableCell align="right">{t('acc:statements.previous')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows
                    .filter((row) => row.section === section)
                    .map((row) => (
                      <TableRow key={`${section}-${row.code}`} data-testid="acc-statements-row">
                        <TableCell>
                          <bdi dir="ltr">{row.code}</bdi>
                        </TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell align="right">
                          <Amount money={row.amount} />
                        </TableCell>
                        <TableCell align="right">
                          {row.previousAmount ? <Amount money={row.previousAmount} /> : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </>
      ) : null}
    </Stack>
  );
}

function AccountLedgerTab({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const [accountId, setAccountId] = useState('');
  const [detailId, setDetailId] = useState('');
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const details = useQuery({
    queryKey: accKeys.details(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/detail-accounts')),
  });
  const ledger = useQuery({
    queryKey: accKeys.ledger(session?.tenantId, `${accountId}|${detailId}|${from}|${to}`),
    enabled: accountId !== '',
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/account-ledger', {
          params: {
            query: { accountId, detailId: detailId || undefined, from, to, limit: 200 },
          },
        }),
      ),
  });
  const leafAccounts = (accounts.data?.items ?? []).filter(
    (account) =>
      account.isActive && !(accounts.data?.items ?? []).some((child) => child.parentId === account.id),
  );
  const data = ledger.data;
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label={t('acc:journal.account')}
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
          sx={{ minWidth: 260 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-ledger-account' } }}
        >
          <MenuItem value="">{t('acc:common.none')}</MenuItem>
          {leafAccounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              <bdi dir="ltr">{account.code}</bdi>&nbsp;{account.title}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('acc:journal.details')}
          value={detailId}
          onChange={(event) => setDetailId(event.target.value)}
          sx={{ minWidth: 220 }}
          slotProps={{ htmlInput: { 'data-testid': 'acc-ledger-detail' } }}
        >
          <MenuItem value="">{t('acc:common.all')}</MenuItem>
          {(details.data?.items ?? [])
            .filter((detail) => detail.isActive)
            .map((detail) => (
              <MenuItem key={detail.id} value={detail.id}>
                <bdi dir="ltr">{detail.code}</bdi>&nbsp;{detail.title}
              </MenuItem>
            ))}
        </TextField>
      </Stack>
      {accountId === '' ? <EmptyState titleKey="acc:ledger.pick_account" /> : null}
      {ledger.isPending && accountId !== '' ? <LoadingState rows={4} /> : null}
      {ledger.error ? <ErrorState error={ledger.error} onRetry={() => void ledger.refetch()} /> : null}
      {data ? (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="acc-ledger">
          <Typography variant="subtitle1" gutterBottom>
            <bdi dir="ltr">{data.accountCode}</bdi> {data.accountTitle}
          </Typography>
          <Table size="small" aria-label={t('acc:ledger.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('acc:common.date')}</TableCell>
                <TableCell>{t('acc:journal.number')}</TableCell>
                <TableCell>{t('acc:journal.description')}</TableCell>
                <TableCell align="right">{t('acc:journal.debit')}</TableCell>
                <TableCell align="right">{t('acc:journal.credit')}</TableCell>
                <TableCell align="right">{t('acc:ledger.balance')}</TableCell>
                <TableCell align="right">{t('acc:journal.sources')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow data-testid="acc-ledger-opening">
                <TableCell colSpan={5}>{t('acc:ledger.opening')}</TableCell>
                <TableCell align="right">
                  <Amount money={data.openingBalance} testId="acc-ledger-opening-balance" />
                </TableCell>
                <TableCell />
              </TableRow>
              {data.rows.map((row) => (
                <TableRow key={row.journalLineId} data-testid="acc-ledger-row">
                  <TableCell>
                    <JalaliDate value={row.businessDate} />
                  </TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={journalPath(row.journalEntryId)}>
                      <bdi dir="ltr">{row.journalNumber}</bdi>
                    </Link>
                  </TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell align="right">
                    {num(row.debit.amount) > 0 ? <Amount money={row.debit} /> : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {num(row.credit.amount) > 0 ? <Amount money={row.credit} /> : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Amount money={row.balance} testId="acc-ledger-balance" />
                  </TableCell>
                  <TableCell align="right">{faDigits(num(row.sourceCount))}</TableCell>
                </TableRow>
              ))}
              <TableRow data-testid="acc-ledger-closing">
                <TableCell colSpan={5}>{t('acc:ledger.closing')}</TableCell>
                <TableCell align="right">
                  <Amount money={data.closingBalance} testId="acc-ledger-closing-balance" />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      ) : null}
    </Stack>
  );
}
